import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  errorHandler, 
  ERROR_SEVERITY,
  RECOVERY_STRATEGIES,
  getRecoveryStrategy 
} from './errorHandling';


// Error notification component
export const ErrorNotification = ({ 
  error, 
  onDismiss, 
  onRetry, 
  onFallback, 
  onReauth,
  showDetails = false 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  
  if (!error) return null;

  const classified = errorHandler.classifyError(error);
  const recoveryStrategy = getRecoveryStrategy(error);

  const handleRetry = async () => {
    if (onRetry) {
      setIsRetrying(true);
      try {
        await onRetry();
      } finally {
        setIsRetrying(false);
      }
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case ERROR_SEVERITY.LOW: return '💡';
      case ERROR_SEVERITY.MEDIUM: return '⚠️';
      case ERROR_SEVERITY.HIGH: return '🚨';
      case ERROR_SEVERITY.CRITICAL: return '🔥';
      default: return '❓';
    }
  };

  const getSeverityClass = (severity) => {
    switch (severity) {
      case ERROR_SEVERITY.LOW: return 'error-low';
      case ERROR_SEVERITY.MEDIUM: return 'error-medium';
      case ERROR_SEVERITY.HIGH: return 'error-high';
      case ERROR_SEVERITY.CRITICAL: return 'error-critical';
      default: return 'error-unknown';
    }
  };

  return (
    <div className={`error-notification ${getSeverityClass(classified.severity)}`}>
      <div className="error-header">
        <span className="error-icon">{getSeverityIcon(classified.severity)}</span>
        <span className="error-message">{classified.userMessage}</span>
        <div className="error-actions">
          {recoveryStrategy === RECOVERY_STRATEGIES.RETRY && onRetry && (
            <button 
              onClick={handleRetry} 
              disabled={isRetrying}
              className="error-action-button retry"
            >
              {isRetrying ? '⏳ Retrying...' : '🔄 Retry'}
            </button>
          )}
          {recoveryStrategy === RECOVERY_STRATEGIES.FALLBACK_LOCAL && onFallback && (
            <button 
              onClick={onFallback}
              className="error-action-button fallback"
            >
              📱 Use Offline Mode
            </button>
          )}
          {recoveryStrategy === RECOVERY_STRATEGIES.REAUTH && onReauth && (
            <button 
              onClick={onReauth}
              className="error-action-button reauth"
            >
              🔐 Log In Again
            </button>
          )}
          {showDetails && (
            <button 
              onClick={() => setIsExpanded(!isExpanded)}
              className="error-action-button details"
            >
              {isExpanded ? '▲ Less' : '▼ Details'}
            </button>
          )}
          <button 
            onClick={onDismiss}
            className="error-action-button dismiss"
          >
            ✕
          </button>
        </div>
      </div>
      
      {isExpanded && (
        <div className="error-details">
          <div className="error-detail-item">
            <strong>Type:</strong> {classified.type}
          </div>
          <div className="error-detail-item">
            <strong>Severity:</strong> {classified.severity}
          </div>
          <div className="error-detail-item">
            <strong>Retryable:</strong> {classified.retryable ? 'Yes' : 'No'}
          </div>
          <div className="error-detail-item">
            <strong>Recovery Strategy:</strong> {recoveryStrategy}
          </div>
          {error.message && (
            <div className="error-detail-item">
              <strong>Technical Details:</strong> {error.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
};





// Error boundary for React error handling
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    console.error('React Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <h2>🚨 Something went wrong</h2>
            <p>The application encountered an unexpected error.</p>
            
            <div className="error-boundary-actions">
              <button 
                onClick={() => window.location.reload()}
                className="error-boundary-button"
              >
                🔄 Reload Page
              </button>
              
              <button 
                onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
                className="error-boundary-button secondary"
              >
                🔙 Try Again
              </button>
            </div>
            
            {this.props.showDetails && this.state.error && (
              <details className="error-boundary-details">
                <summary>Technical Details</summary>
                <pre className="error-boundary-stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}